import { useEffect, useRef, useState } from "react";
import { Loader2, Stethoscope, Wrench, CheckCircle2, ExternalLink, ShieldAlert, AlignCenterHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useWorkspace } from "../../store/WorkspaceContext";
import { NameFixesPanel } from "./NameFixesPanel";

const KIND_COLOR = { dropped_curve: "#e5484d", fold: "#e5484d", loop: "#e5484d", spike: "#f5a524", bulge: "#f5a524", offset: "#f0c945", global: "#e5484d" };

const Issue = ({ i, k }) => (
  <span data-testid={`health-issue-${k}`} className="rounded-full px-2 py-px font-mono text-[10px]" style={{ background: `${KIND_COLOR[i.kind] || "#9aa0a8"}22`, color: KIND_COLOR[i.kind] || "#9aa0a8" }}>
    {i.label}{i.dev ? ` ${Number(i.dev).toFixed(2)}"` : ""}
  </span>
);

const SecurityEvents = () => {
  const [events, setEvents] = useState(null);
  useEffect(() => { api.adminSecurity().then(setEvents).catch(() => setEvents([])); }, []);
  return (
    <div className="glass-row p-3" data-testid="security-events">
      <div className="flex items-center gap-2 text-[12px] font-semibold"><ShieldAlert className="h-4 w-4 text-[#e5484d]" /> Security · blocked tampering <span className="font-normal text-[#6b7079]">— you are emailed for each new kind (max once an hour)</span></div>
      {events === null ? <div className="mt-2 text-[11px] text-[#9aa0a8]"><Loader2 className="inline h-3 w-3 animate-spin" /> Loading…</div>
        : events.length === 0 ? <div className="mt-2 text-[12px] text-[#3ecf8e]" data-testid="security-clean">No tampering detected.</div>
        : <div className="scroll-visible mt-2 max-h-56 overflow-y-auto">{events.map((e, i) => (
          <div key={i} className="flex items-center gap-3 border-t border-white/5 py-1.5 text-[11px]" data-testid={`security-event-${i}`}>
            <span className="w-32 flex-none font-mono text-[#9aa0a8]">{new Date(e.created_at).toLocaleString()}</span>
            <span className="min-w-0 flex-1 truncate"><b className="text-[#ff6b70]">{e.title}</b>{e.detail ? ` · ${e.detail}` : ""}</span>
            <span className="flex-none font-mono text-[#6b7079]">{e.email || e.ip || "—"}</span>
          </div>))}</div>}
    </div>
  );
};

export const AdminHealthTab = () => {
  const { dispatch } = useWorkspace();
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fixing, setFixing] = useState(null); // "all" | pattern id
  const [fixed, setFixed] = useState({});

  const [progress, setProgress] = useState(null);
  const timer = useRef(null);
  const poll = async () => {
    try {
      const st = await api.libraryHealth();
      if (st.status === "running") { setProgress([st.progress, st.total]); timer.current = setTimeout(poll, 2000); return; }
      if (st.status === "error") toast.error(`Scan failed: ${st.error}`);
      if (st.result) setScan(st.result);
      setBusy(false);
    } catch (e) { toast.error(apiError(e, "Scan failed")); setBusy(false); }
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  const [squaring, setSquaring] = useState(false);
  const squareUp = async () => {
    setSquaring(true);
    try {
      const r = await api.librarySquareUp();
      if (r.fixed) toast.success(`Straightened ${r.fixed} centre pattern${r.fixed === 1 ? "" : "s"}: ${r.fixes.map((f) => `${f.name} (${f.tilt > 0 ? "+" : ""}${f.tilt}°)`).join(", ")}`, { duration: 12000 });
      else toast.success(`All ${r.checked} centre-glass patterns are already straight`);
    } catch (e) { toast.error(apiError(e, "Straighten failed")); } finally { setSquaring(false); }
  };
  useEffect(() => { api.libraryHealth().then((st) => { if (st.status === "running") { setBusy(true); poll(); } else if (st.result) setScan(st.result); }).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async () => {
    setBusy(true); setFixed({}); setProgress(null);
    try { await api.libraryHealthStart(); timer.current = setTimeout(poll, 1500); } catch (e) { toast.error(apiError(e, "Scan failed")); setBusy(false); }
  };
  const fix = async (ids, all = false) => {
    setFixing(all ? "all" : ids[0]);
    try {
      const r = await api.libraryHealthFix(ids, all);
      const done = {};
      r.fixed.forEach((f) => { done[f.id] = f; });
      setFixed((x) => ({ ...x, ...done }));
      dispatch({ type: "LIBRARY_CHANGED" });
      toast.success(r.fixed.length ? `${r.fixed.length} template${r.fixed.length === 1 ? "" : "s"} repaired — saved as new versions (old ones stay in history)` : "Nothing needed changing");
    } catch (e) { toast.error(apiError(e, "Fix failed")); }
    setFixing(null);
  };

  const groups = {};
  (scan?.patterns || []).forEach((p) => { (groups[p.vehicle] = groups[p.vehicle] || []).push(p); });
  const open = (p) => dispatch({ type: "SET", patch: { loadVehicleId: p.vehicle_id, dialog: null } });

  return (
    <div className="space-y-3 px-5 py-3" data-testid="admin-health-tab">
      <SecurityEvents />
      <NameFixesPanel />
      <div className="glass-row flex flex-wrap items-center gap-3 p-3">
        <Stethoscope className="h-4 w-4 text-[#4c9ffe]" />
        <div className="min-w-0 flex-1 text-[12px]">
          <div className="font-medium text-[#e8e9ec]">Library health scan</div>
          <div className="text-[11px] text-[#9aa0a8]">Every template is checked against its mirror (left ↔ right, centre glass ↔ itself) and for spikes, loops, folds and dropped curves. Designs are never changed: only unambiguous digitising errors get a <b>Fix</b> button (the bad stretch is replaced, everything else stays exactly as stored); anything that could be a real feature — a wiper or sensor notch, a dent, sides that differ slightly — is listed for <b>review</b> only.</div>
        </div>
        <button data-testid="health-scan-btn" onClick={run} disabled={busy} className="glass-btn flex h-8 items-center gap-1.5 px-3 text-[11px]">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Stethoscope className="h-3.5 w-3.5 text-[#4c9ffe]" />} {scan ? "Scan again" : "Scan library"}</button>
        <button data-testid="health-square-up-btn" onClick={squareUp} disabled={squaring} title="Rotate every tilted windshield / back glass / roof so its mirror axis is exactly straight (rotation only, size untouched)" className="glass-btn flex h-8 items-center gap-1.5 px-3 text-[11px]">{squaring ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <AlignCenterHorizontal className="h-3.5 w-3.5 text-[#f0c945]" />} Straighten centre glass</button>
        {scan?.fixable > 0 && <button data-testid="health-fix-all-btn" onClick={() => fix([], true)} disabled={Boolean(fixing)} className="btn-red flex h-8 items-center gap-1.5 px-3 text-[11px]">{fixing === "all" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wrench className="h-3.5 w-3.5" />} Fix all ({scan.fixable})</button>}
      </div>
      {busy && <div className="p-6 text-center text-[12px] text-[#9aa0a8]" data-testid="health-scanning"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin text-[#4c9ffe]" />Scanning every template — this takes about a minute…{progress ? <span data-testid="health-progress" className="ml-1 font-mono">{progress[0]} / {progress[1]}</span> : null}</div>}
      {scan && !busy && (
        <>
          <div className="flex items-center gap-2 text-[12px]" data-testid="health-summary">
            {scan.flagged ? <span className="text-[#f5a524]">{scan.checked} templates checked · {scan.fixable} with a clear defect to fix · {scan.flagged - scan.fixable} to review (left as designed)</span> : <span className="flex items-center gap-1.5 text-[#3ecf8e]"><CheckCircle2 className="h-4 w-4" /> All {scan.checked} templates are clean</span>}
          </div>
          {Object.entries(groups).map(([veh, pats]) => (
            <div key={veh} className="glass-row overflow-hidden" data-testid={`health-vehicle-${pats[0].vehicle_id}`}>
              <div className="flex items-center justify-between border-b border-white/10 px-3 py-1.5 text-[11px] font-medium text-[#e8e9ec]">{veh}<button data-testid={`health-open-${pats[0].vehicle_id}`} onClick={() => open(pats[0])} className="flex items-center gap-1 text-[10px] text-[#9aa0a8] hover:text-white"><ExternalLink className="h-3 w-3" /> Open</button></div>
              <ul className="divide-y divide-white/5">
                {pats.map((p) => (
                  <li key={p.id} data-testid={`health-row-${p.id}`} className="flex flex-wrap items-center gap-2 px-3 py-1.5 text-[12px]">
                    <span className="min-w-[160px] text-[#c7cbd1]">{p.name}</span>
                    <span className="flex flex-wrap items-center gap-1">{p.issues.map((i, k) => <Issue key={k} i={i} k={`${p.id}-${k}`} />)}</span>
                    <span className="ml-auto font-mono text-[10px] text-[#6b7079]" title="Mismatch before → after repair">{p.dev_max ? `${p.dev_max.toFixed(2)}" → ${p.dev_after.toFixed(2)}"` : ""}</span>
                    {fixed[p.id] ? <span data-testid={`health-fixed-${p.id}`} className="flex items-center gap-1 text-[11px] text-[#3ecf8e]"><CheckCircle2 className="h-3.5 w-3.5" /> v{fixed[p.id].version}</span>
                      : !p.fixable ? <span data-testid={`health-review-${p.id}`} title="Could be a real feature of the glass — left exactly as stored. Open the vehicle to check it." className="rounded-full border border-[#27324a] px-2 py-0.5 text-[10px] text-[#9aa0a8]">review</span>
                      : <button data-testid={`health-fix-${p.id}`} onClick={() => fix([p.id])} disabled={Boolean(fixing)} className="glass-btn flex h-7 items-center gap-1 px-2.5 text-[11px]">{fixing === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wrench className="h-3 w-3" />} Fix</button>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </>
      )}
      {!scan && !busy && <div className="p-6 text-center text-[12px] text-[#6b7079]" data-testid="health-idle">Press “Scan library” to check every template.</div>}
    </div>
  );
};
